import {TypeGuard} from "../shared-pipes/type-guard.pipe";
import {
	DialogData_ImageDialogInput,
	DialogData_ImageDialogMediaInput,
	DialogData_ImageDialogUrlInput,
} from "../../modules/dialog/material-dialogs/general-dialogs/image-dialog/image-dialog.component";
import {
	AssessableItem,
	Assessment,
	AssessmentItem,
	BasePeerPracticeApplicationData,
	Checklist,
	ChecklistItem,
	ExamApplicationData,
	ExamApplicationDataType,
	ExamMasterOfCeremonyApplicationData,
	ExamMasterOfCeremonyRunApplicationData,
	ExamPhase,
	ExamPhaseEncounter,
	ExamRunApplicationData,
	ExamRunEncounterApplicationData,
	ExamSession,
	IApplicationData,
	ISimSession,
	MultiSelectColumn,
	NonSimulationSession,
	SearchableColumn,
	SelectColumn,
	SessionPhaseType,
	SessionType,
	TeamTrainingSession,
	UserRecordingAssessment,
	UserRecordingChecklist,
} from "@models";
// Do not import from @services here, otherwise you will get a circular dependency
// noinspection ES6PreferShortImport
import {GeneratedTypePredicates} from "../../api/generated/service/generated-type-predicates";

/**
 * @deprecated Please use GeneratedTypePredicates instead
 */
export class TypePredicates {
	static isAssessmentItem(item: AssessableItem): item is AssessmentItem {
		return (item as AssessmentItem).assessmentItemId !== undefined;
	}

	static isChecklistItem(item: AssessableItem): item is ChecklistItem {
		return (item as ChecklistItem).checklistItemId !== undefined;
	}

	static isAssessment(value: object): value is Assessment {
		return (value as Assessment).passMark !== null && (value as Assessment).passMark !== undefined;
	}

	static isUserRecordingAssessment(value: object): value is UserRecordingAssessment {
		return (value as UserRecordingAssessment).assignedCompetenceCategories !== undefined;
	}

	static isUserRecordingChecklist(value: object): value is UserRecordingChecklist {
		let userRecordingChecklist = value as UserRecordingChecklist;
		return userRecordingChecklist.scenarioRecordingId !== undefined && userRecordingChecklist.checklistId !== undefined;
	}

	static isExamSession(session: ISimSession): session is ExamSession {
		return session.type === SessionType.Exam;
	}

	static isTeamTrainingSession(session: ISimSession): session is TeamTrainingSession {
		return session.type === SessionType.TeamTrainingSession;
	}

	static isNonSimulationSession(session: ISimSession): session is NonSimulationSession {
		return session.type === SessionType.NonSimulation;
	}

	static isChecklist(value: object): value is Checklist {
		return !TypePredicates.isAssessment(value);
	}

	static isMediaDialogInput: TypeGuard<DialogData_ImageDialogInput, DialogData_ImageDialogMediaInput> = (
		value,
	): value is DialogData_ImageDialogMediaInput => {
		return value.type === "media";
	};

	static isUrlDialogInput: TypeGuard<DialogData_ImageDialogInput, DialogData_ImageDialogUrlInput> = (
		value,
	): value is DialogData_ImageDialogUrlInput => {
		return value.type === "url";
	};

	static isExamPhaseEncounter(phase: ExamPhase): phase is ExamPhaseEncounter {
		return phase.type === SessionPhaseType.Encounter;
	}

	static isExamRunEncounterApplicationData(examState: ExamRunApplicationData): examState is ExamRunEncounterApplicationData {
		return examState.currentExamPhase.type === SessionPhaseType.Encounter;
	}

	static isExamRunApplicationData(examState: IApplicationData | null): examState is ExamRunApplicationData {
		return [
			ExamApplicationDataType.StudentReady,
			ExamApplicationDataType.PreEncounter,
			ExamApplicationDataType.Encounter,
			ExamApplicationDataType.PostEncounter,
		].includes((examState as ExamApplicationData)?.type);
	}

	static isExamApplicationActiveData(examState: IApplicationData): examState is ExamApplicationData {
		return ![ExamApplicationDataType.Idle].includes((examState as ExamApplicationData)?.type);
	}

	static isExamApplicationData(examState: IApplicationData): examState is ExamApplicationData {
		return [
			ExamApplicationDataType.StudentReady,
			ExamApplicationDataType.PreEncounter,
			ExamApplicationDataType.Encounter,
			ExamApplicationDataType.PostEncounter,
			ExamApplicationDataType.Closing,
			ExamApplicationDataType.ConfirmationRunning,
			ExamApplicationDataType.ConfirmationClosed,
			ExamApplicationDataType.Idle,
		].includes((examState as ExamApplicationData)?.type);
	}

	static isExamMcRunApplicationData(examState: ExamMasterOfCeremonyApplicationData): examState is ExamMasterOfCeremonyRunApplicationData {
		return [
			ExamApplicationDataType.StudentReady,
			ExamApplicationDataType.PreEncounter,
			ExamApplicationDataType.Encounter,
			ExamApplicationDataType.PostEncounter,
		].includes(examState?.type);
	}

	static isSelectColumn(column: SearchableColumn): column is SelectColumn {
		return column.type === "Select";
	}

	static isMultiSelectColumn(column: SearchableColumn): column is MultiSelectColumn {
		return column.type === "MultiSelect";
	}

	static isBasePeerPracticeApplicationData(applicationData: IApplicationData): applicationData is BasePeerPracticeApplicationData {
		return (
			GeneratedTypePredicates.isPeerPracticeIntermissionApplicationData(applicationData) ||
			GeneratedTypePredicates.isPeerPracticeUpcomingSessionApplicationData(applicationData) ||
			GeneratedTypePredicates.isPeerPracticeEncounterApplicationData(applicationData) ||
			GeneratedTypePredicates.isPeerPracticeConfirmationApplicationData(applicationData) ||
			GeneratedTypePredicates.isPeerPracticeClosingApplicationData(applicationData)
		);
	}

	static isDefined<T>(value: T | undefined | null): value is T {
		return value !== undefined && value !== null;
	}
}
